/**
 * Chat State Hook
 * Manages all state for chat/messaging functionality
 */

import { useState, useRef } from 'react';

export const useChatState = () => {
  // Conversation state
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationsLoading, setConversationsLoading] = useState(true);

  // Message state
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [loading, setLoading] = useState(false);

  // Lazy pagination ("load older messages on scroll up")
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);

  // Read receipts
  const [readReceipts, setReadReceipts] = useState({});
  const [otherLastReadMessageId, setOtherLastReadMessageId] = useState(null);
  const [otherLastReadMessageCreatedAt, setOtherLastReadMessageCreatedAt] = useState(null);
  const [selfLastReadMessageId, setSelfLastReadMessageId] = useState(null);
  const [firstUnreadIndex, setFirstUnreadIndex] = useState(null);

  // Typing indicators
  const [typing, setTyping] = useState('');
  const [typingByConversation, setTypingByConversation] = useState(new Map());

  // Voice-recording indicators (mirrors typing)
  const [voiceRecordingByConversation, setVoiceRecordingByConversation] = useState(new Map());

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState(false);
  const [searchType, setSearchType] = useState('user');
  const [localMatches, setLocalMatches] = useState([]);
  const [globalMatches, setGlobalMatches] = useState([]);
  const [showTypeToggle, setShowTypeToggle] = useState(false);

  // User state
  const [currentUser, setCurrentUser] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [onlineUsernames, setOnlineUsernames] = useState([]);
  const [userPresence, setUserPresence] = useState(new Map());

  // UI state
  const [selectedUserTarget, setSelectedUserTarget] = useState(null);
  const [groupPreview, setGroupPreview] = useState(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  // Refs
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const selectedConversationRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isTypingRef = useRef(false);
  // Highest message page currently loaded for the open conversation (0 = newest page).
  const messagesPageRef = useRef(0);
  // Guards against firing overlapping "load older" requests while one is in flight.
  const loadingMoreRef = useRef(false);
  // Holds pre-prepend scroll metrics so we can restore the viewport after older
  // messages are inserted above the current view (set in ChatInterface).
  const prependRestoreRef = useRef(null);

  return {
    // Conversation state
    conversations, setConversations,
    selectedConversation, setSelectedConversation,
    conversationsLoading, setConversationsLoading,

    // Message state
    messages, setMessages,
    messageInput, setMessageInput,
    loading, setLoading,

    // Lazy pagination
    loadingMore, setLoadingMore,
    hasMoreMessages, setHasMoreMessages,

    // Read receipts
    readReceipts, setReadReceipts,
    otherLastReadMessageId, setOtherLastReadMessageId,
    otherLastReadMessageCreatedAt, setOtherLastReadMessageCreatedAt,
    selfLastReadMessageId, setSelfLastReadMessageId,
    firstUnreadIndex, setFirstUnreadIndex,

    // Typing
    typing, setTyping,
    typingByConversation, setTypingByConversation,

    // Voice recording
    voiceRecordingByConversation, setVoiceRecordingByConversation,

    // Search
    searchTerm, setSearchTerm,
    searchMode, setSearchMode,
    searchType, setSearchType,
    localMatches, setLocalMatches,
    globalMatches, setGlobalMatches,
    showTypeToggle, setShowTypeToggle,

    // User state
    currentUser, setCurrentUser,
    isConnected, setIsConnected,
    onlineUsernames, setOnlineUsernames,
    userPresence, setUserPresence,

    // UI state
    selectedUserTarget, setSelectedUserTarget,
    groupPreview, setGroupPreview,
    showScrollToBottom, setShowScrollToBottom,

    // Refs
    messagesEndRef,
    containerRef,
    selectedConversationRef,
    typingTimeoutRef,
    isTypingRef,
    messagesPageRef,
    loadingMoreRef,
    prependRestoreRef
  };
};

export default useChatState;

